import axios from 'axios';
import * as moment from 'moment';
import {isEmpty} from 'lodash';
import {v4} from 'uuid';
import * as BatchWaving from '../../warehouse/ecommerce-batches/BatchInterfaces';
import {ApiRequest, ApiResponse, Warehouse} from '../CommonInterfaces';
import {UnresolvedWavingAlertsResponse} from '../../warehouse/ecommerce-batches/BatchInterfaces';
import {
  GetWaveResponse,
  GetWavesResponse,
  GetWavingSummaryRequest,
  GetWavingSummaryResponse,
  TerminateWaveRequest
} from '../../warehouse/batch-waving/WaveInterfaces';
import {
  FilterKeys,
  FilterParams,
  GetBatchCountsResponse,
  GetBatchesResponse,
  GetBatchResponse,
  PickWaveStatus
} from '../../warehouse/batch-waving/WaveBatchInterfaces';
import {CarrierReservationTotalsRequest} from '../../warehouse/batch-waving/wave-templates/WaveTemplateInterfaces';
import InternalAPIService from './InternalAPIService';

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
class BatchWavingService extends InternalAPIService {
  public currentWarehouse?: number;
  public selectedWarehouse?: Warehouse;
  private ecommUrl: string = '/wh/fulfillment/ecommerce';

  constructor(authenticityToken: string) {
    super(authenticityToken, '/wh/wave');
  }

  public async getOrders(
    filters: BatchWaving.OrderFilters,
    continuationToken?: string,
    pageSize?: number,
    sortOrder?: string
  ): Promise<ApiResponse<BatchWaving.OrdersResponse>> {
    const requestParameters: ApiRequest<BatchWaving.BatchOrdersRequest> = {
      authenticity_token: this.authenticityToken,
      meta: {
        correlationId: v4()
      },
      data: {
        continuationToken,
        filters,
        pageSize,
        sortOrder
      }
    };
    try {
      const response = await axios.post(`${this.ecommUrl}/orders`, requestParameters);
      return response.data as ApiResponse<BatchWaving.OrdersResponse>;
    } catch (error) {
      return this.handleErrorResponse(error);
    }
  }

  public async getShipments(
    filters: BatchWaving.OrderFilters,
    continuationToken?: string,
    pageSize?: number,
    sortOrder?: string
  ): Promise<ApiResponse<BatchWaving.OrdersResponse>> {
    const requestParameters: ApiRequest<BatchWaving.BatchOrdersRequest> = {
      authenticity_token: this.authenticityToken,
      meta: {
        correlationId: v4()
      },
      data: {
        continuationToken,
        filters,
        pageSize,
        sortOrder
      }
    };
    try {
      const response = await axios.post(`${this.ecommUrl}/shipments`, requestParameters);
      return response.data as ApiResponse<BatchWaving.OrdersResponse>;
    } catch (error) {
      return this.handleErrorResponse(error);
    }
  }

  public async getOrder(orderId: number): Promise<ApiResponse<BatchWaving.OrderDetail>> {
    try {
      const response = await axios.get(`${this.ecommUrl}/order/${orderId}/details`);
      return response.data as ApiResponse<BatchWaving.OrderDetail>;
    } catch (error) {
      return this.handleErrorResponse(error);
    }
  }

  public async skuTypeAhead(reservationId: number, skuQuery: string): Promise<ApiResponse<BatchWaving.SkuData>> {
    const requestParameters: ApiRequest<BatchWaving.SkuTypeAheadRequest> = {
      authenticity_token: this.authenticityToken,
      meta: {
        correlationId: v4()
      },
      data: {
        reservationId,
        skuSubstring: skuQuery
      }
    };
    try {
      const response = await axios.post(`${this.baseUrl}/sku_type_ahead`, requestParameters);
      return response.data as ApiResponse<BatchWaving.SkuData>;
    } catch (error) {
      return this.handleErrorResponse(error);
    }
  }

  public async getCarrierInfo(reservationId: number): Promise<ApiResponse<BatchWaving.CarrierResponse>> {
    const requestParameters: ApiRequest<any> = {
      authenticity_token: this.authenticityToken,
      meta: {
        correlationId: v4()
      },
      data: {
        reservationId
      }
    };
    try {
      const response = await axios.post(`${this.baseUrl}/new_order_carrier_types`, requestParameters);
      return response.data as ApiResponse<BatchWaving.CarrierResponse>;
    } catch (error) {
      return this.handleErrorResponse(error);
    }
  }

  public async getActiveShipOnDates(warehouseId: number): Promise<ApiResponse<BatchWaving.ActiveShipOnDatesResponse>> {
    try {
      const response = await axios.get(`${this.baseUrl}/active_ship_on_dates`, {
        params: {
          warehouseId
        }
      });
      return response.data as ApiResponse<BatchWaving.ActiveShipOnDatesResponse>;
    } catch (error) {
      return this.handleErrorResponse(error);
    }
  }

  /**
   * Fetch the carrier reservation totals for a warehouse.
   * @param warehouseId ID of the warehouse to totals for.
   * @param cutoffDate
   * @param useexactShipOnDates if true, use the "exactShipOnDates" parameter instead of "shipByDate" to get only
   * shipments with the specified ship by date, otherwise uses the "all dates up to cutoff date" behavior.
   */
  public async getCarrierReservationTotal(
    warehouseId: number,
    cutoffDate: string,
    useexactShipOnDates: boolean
  ): Promise<ApiResponse<Map<string, Map<number, number>>>> {
    const data: CarrierReservationTotalsRequest = {warehouseId};
    const dateStr = moment(cutoffDate).format('YYYY-MM-DD');
    if (useexactShipOnDates) {
      data.exactShipOnDates = [dateStr];
    } else {
      data.cutoffDate = dateStr;
    }
    try {
      const response = await axios.get(`${this.baseUrl}/carrier_reservation_total`, {params: data});
      return response.data as Promise<ApiResponse<Map<string, Map<number, number>>>>;
    } catch (error) {
      return this.handleErrorResponse(error);
    }
  }

  public async waveByShipmentIds(
    waveParams: BatchWaving.WaveByShipmentIdsParams
  ): Promise<ApiResponse<BatchWaving.WaveByShipmentIdsResponse>> {
    const requestParameters = {
      authenticity_token: this.authenticityToken,
      meta: {
        correlationId: v4()
      },
      data: {
        wave: waveParams
      }
    };

    try {
      const response = await axios.post(`${this.baseUrl}/batch_by_shipment_ids`, requestParameters);
      return response.data as ApiResponse<BatchWaving.WaveByShipmentIdsResponse>;
    } catch (error) {
      return error.response.status === cloudflareTimeoutResponseCode
        ? {
            meta: {
              correlationId: '',
              responseCreatedAt: ''
            },
            links: {
              self: ''
            },
            data: undefined,
            errors: [
              {
                status: cloudflareTimeoutResponseCode,
                title: 'The wave request timed out.',
                detail: `The Shipment${
                  waveParams.shipmentIds.length !== 1 ? 's are' : ' is'
                  // eslint-disable-next-line max-len
                } taking longer than expected to wave. Please reload the page to see the wave status and try again if your Shipment${
                  waveParams.shipmentIds.length !== 1 ? "s aren't" : " isn't"
                } waved.`,
                source: undefined
              }
            ]
          }
        : this.handleErrorResponse(error);
    }
  }

  public async previewWave(wave: BatchWaving.WaveParameters): Promise<ApiResponse<BatchWaving.PreviewResponse>> {
    const requestParameters: ApiRequest<BatchWaving.BatchRequest> = {
      authenticity_token: this.authenticityToken,
      meta: {
        correlationId: v4()
      },
      data: {
        wave
      }
    };
    try {
      const response = await axios.post(`${this.baseUrl}/preview_batches`, requestParameters);
      return response.data as ApiResponse<BatchWaving.PreviewResponse>;
    } catch (error) {
      return this.handleErrorResponse(error);
    }
  }

  public async immediateBatch(wave: BatchWaving.WaveParameters): Promise<ApiResponse<BatchWaving.CreateResponse>> {
    const requestParameters: ApiRequest<BatchWaving.BatchRequest> = {
      authenticity_token: this.authenticityToken,
      meta: {
        correlationId: v4()
      },
      data: {
        wave
      }
    };
    try {
      const response = await axios.post(`${this.baseUrl}/immediate_batch`, requestParameters);
      return response.data as ApiResponse<BatchWaving.CreateResponse>;
    } catch (error) {
      return this.handleErrorResponse(error);
    }
  }

  public async getWavingSummary(
    warehouseId: number,
    currentDate: string
  ): Promise<ApiResponse<GetWavingSummaryResponse>> {
    const query: GetWavingSummaryRequest = {
      warehouseId,
      currentDate
    };

    return this.makeGetRequest(`${this.ecommUrl}/waving_summary`, query);
  }

  public async getAllCarriersAndServiceTypes(): Promise<
    ApiResponse<{
      carriers: string[];
      serviceTypes: string[];
      mappedShippingInfo: object;
    }>
  > {
    return await this.makeGetRequest(`${this.ecommUrl}/carrier_service_info`);
  }

  // Waving Alerts
  public async getUnresolvedWavingAlerts(warehouseId): Promise<ApiResponse<UnresolvedWavingAlertsResponse>> {
    const query = {
      warehouseId
    };
    return this.makeGetRequest(`${this.ecommUrl}/alerts/unresolved_alerts`, query);
  }

  public async markAlertsAsRead(alertIds: number[]): Promise<ApiResponse<any>> {
    const payload = {
      batchErrorIds: alertIds
    };
    return this.makePatchRequest(`${this.ecommUrl}/alerts/mark_alerts_as_read`, payload);
  }

  public async resolveAlerts(alertIds: number[]): Promise<ApiResponse<any>> {
    const payload = {
      ids: alertIds
    };
    return this.makePatchRequest(`${this.ecommUrl}/alerts/resolve`, payload);
  }

  public async getWaves(
    reservationIds,
    filterParams: FilterParams,
    continuationToken,
    pageSize
  ): Promise<ApiResponse<GetWavesResponse>> {
    let searchString = '';

    searchString = this.appendParameter(searchString, 'reservationIds', reservationIds || []);
    if (this.currentWarehouse) {
      const prepend = isEmpty(searchString) ? '?' : '&';
      searchString = `${searchString}${prepend}warehouseId=${this.currentWarehouse}`;
    }
    if (filterParams[FilterKeys.WaveId]) {
      searchString = this.appendParameter(searchString, 'waveIds', [filterParams[FilterKeys.WaveId]]);
    }
    if (filterParams[FilterKeys.ShipmentId]) {
      searchString = this.appendParameter(searchString, 'shipmentIds', [filterParams[FilterKeys.ShipmentId]]);
    }
    if (filterParams[FilterKeys.BatchId]) {
      searchString += `&batchId=${filterParams[FilterKeys.BatchId]}`;
    }
    if (filterParams[FilterKeys.Carrier]) {
      searchString += `&carrier=${filterParams[FilterKeys.Carrier]}`;
    }
    if (filterParams[FilterKeys.ServiceType]) {
      searchString += `&serviceType=${filterParams[FilterKeys.ServiceType]}`;
    }
    if (filterParams[FilterKeys.Status] === 'active') {
      searchString = this.appendParameter(searchString, 'statuses', [PickWaveStatus.new, PickWaveStatus.in_progress]);
    } else if (filterParams[FilterKeys.Status]) {
      searchString = this.appendParameter(searchString, 'statuses', [filterParams[FilterKeys.Status]]);
    }
    if (filterParams[FilterKeys.StagingLocation]) {
      searchString += `&stagingLocation=${filterParams[FilterKeys.StagingLocation]}`;
    }

    if (filterParams[FilterKeys.Mhe]) {
      searchString += `&mhe=${filterParams[FilterKeys.Mhe]}`;
    }

    if (filterParams[FilterKeys.HidePrinted] === true) {
      // For the case where hide_printed is selected as no, we don't need to pass anything in the query params
      searchString += `&hide_printed=true`;
    }

    if (filterParams[FilterKeys.ShipBy]) {
      // TODO FE-577: this looks like a real error that should be fixed
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      searchString += `&shipBy=${filterParams[FilterKeys.ShipBy]}`;
    }
    if (continuationToken) {
      searchString += `&continuationToken=${continuationToken}`;
    }
    if (filterParams[FilterKeys.SortOrder]) {
      searchString += `&sortOrder=${filterParams[FilterKeys.SortOrder]}`;
    }
    if (filterParams[FilterKeys.LoadGroup]) {
      searchString += `&loadGroup=${filterParams[FilterKeys.LoadGroup]}`;
    }
    if (filterParams[FilterKeys.DestinationTag]) {
      searchString += `&destinationTag=${filterParams[FilterKeys.DestinationTag]}`;
    }
    if (filterParams[FilterKeys.ShipMode]) {
      searchString += `&shipMode=${filterParams[FilterKeys.ShipMode]}`;
    }
    if (filterParams[FilterKeys.PurchaseOrder]) {
      searchString += `&purchaseOrder=${filterParams[FilterKeys.PurchaseOrder]}`;
    }
    if (pageSize) {
      searchString += `&pageSize=${pageSize}`;
    }

    return this.makeGetRequest(`${this.baseUrl}/waves${searchString}`);
  }

  public async getWaveBatches(waveId: number, continuationToken?: string): Promise<ApiResponse<GetBatchesResponse>> {
    let searchString = this.appendParameter('', 'waveIds', [waveId]);
    if (continuationToken) {
      searchString += `&continuationToken=${continuationToken}`;
    }
    return this.makeGetRequest(`${this.baseUrl}/pick_batches${searchString}`);
  }

  public async getBatchCountsByWaveIds(waveIds: number[]): Promise<ApiResponse<GetBatchCountsResponse>> {
    const searchString = this.appendParameter('', 'waveIds', waveIds);
    return this.makeGetRequest(`${this.baseUrl}/pick_batch_counts${searchString}`);
  }

  public async getWaveById(waveId: number): Promise<ApiResponse<GetWaveResponse>> {
    const response = await this.makeGetRequest(`${this.baseUrl}/wave?id=${waveId}`);

    if (!response?.data) {
      return response;
    }

    const waveAttr = {};
    for (const curAttr of response.data.wave?.waveAttributes ?? []) {
      waveAttr[translateSnakeToCamel(curAttr.name)] = curAttr.value;
    }

    const copy = {...response};

    if (copy.data?.wave?.waveAttributes) {
      copy.data.wave.waveAttributes = waveAttr;
    }

    return copy;
  }

  public async terminateWaveById(waveId: number, data: TerminateWaveRequest): Promise<ApiResponse<GetWaveResponse>> {
    return this.makePatchRequest(`${this.baseUrl}/terminate?id=${waveId}`, data);
  }

  public async regenWaveDocsById(waveId: number): Promise<ApiResponse<never>> {
    return this.makePostRequest(`${this.baseUrl}/${waveId}/documents/regenerate`, {});
  }

  public async getBatchById(
    batchId: string,
    includeAdditionalInformation: boolean = false
  ): Promise<ApiResponse<GetBatchResponse>> {
    let queryString = `?id=${batchId}`;
    if (includeAdditionalInformation) {
      queryString += '&includeAdditionalInformation=true';
    }
    return this.makeGetRequest(`${this.baseUrl}/pick_batch${queryString}`);
  }

  public async getPickCompleteForFreightWave(shipmentId: number): Promise<ApiResponse<any>> {
    return this.makeGetRequest(`/wh/ui/wave/pick_complete_for_freight_wave?shipment_id=${shipmentId}`);
  }

  private appendParameter(baseString: string, parameterName: string, array: any[]): string {
    if (isEmpty(array)) {
      return baseString;
    }

    for (const item of array) {
      const prepend = isEmpty(baseString) ? '?' : '&';
      baseString = `${baseString}${prepend}${parameterName}[]=${item.toString()}`;
    }
    return baseString;
  }
}

const translateSnakeToCamel = (toTranslate: string): string | undefined => {
  if (!toTranslate) {
    return toTranslate;
  }

  return toTranslate.split('_').reduce((prev, cur) => {
    return `${prev}${cur.charAt(0).toUpperCase()}${cur.substring(1).toLowerCase()}`;
  });
};

// This is a special status code returned by Cloudflare when a timeout occurs
const cloudflareTimeoutResponseCode: number = 524;

export default BatchWavingService;
